import { useCallback, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { chatMessageSchema } from '@mindkeep/shared'
import { ApiError, buildAuthedHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'
import { useWorkspace } from '@/features/workspace/workspace-provider'

const speechRequestSchema = z.object({
  sessionId: z.string().uuid(),
  messageId: z.string().uuid(),
})

export type ReadAloudState = 'idle' | 'loading' | 'ready' | 'error'

export interface UseReadAloudResult {
  state: ReadAloudState
  audioUrl: string | null
  errorMessage: string | null
  /** Ref, die an das <audio>-Element gebunden werden MUSS — stop/reset koennen
   *  sonst die laufende Wiedergabe nicht pausieren. */
  audioRef: React.RefObject<HTMLAudioElement>
  /** Startet die Sprachausgabe. Bei laufendem Request wird abgebrochen. */
  toggle: () => void
  retry: () => void
  /** Stoppt Wiedergabe und gibt die Object-URL frei. */
  reset: () => void
}

/**
 * Explizites Vorlesen (Voxtral TTS 3, EU Mistral) fuer abgeschlossene
 * Assistant-Nachrichten. Kein Autoplay, keine automatische Wiedergabe — der
 * Request laeuft erst nach Klick auf "Vorlesen". Pro Nachricht/Workspace wird
 * hoechstens eine Object-URL gehalten; Abbruch, Unmount und Nachrichten- oder
 * Workspace-Wechsel revoken die URL und brechen den Fetch ab.
 *
 * Der Request-Key nutzt die AKTIVE Workspace-ID (useWorkspace), nicht nur
 * message.workspace_id — so wird bei einem Workspace-Wechsel keine Audio aus
 * einem fremden Workspace weitergespielt.
 */
export function useReadAloud(message: z.infer<typeof chatMessageSchema>): UseReadAloudResult {
  const { workspace } = useWorkspace()
  const activeWorkspaceId = workspace.id

  const [state, setState] = useState<ReadAloudState>('idle')
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const abortRef = useRef<AbortController | null>(null)
  const urlRef = useRef<string | null>(null)
  // Echte Audio-Ref — wird vom Component gebunden und hier fuer stop/reset
  // verwendet, damit laufende Wiedergabe pausiert werden kann.
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // Stabile Request-Identitaet: nur bei Wechsel von Nachricht/Workspace neu laden.
  const requestKeyRef = useRef<string>('')
  // Generation-Counter: nur die neueste Request-Generation darf State setzen
  // und eine Object-URL erzeugen (stale responses duerfen kein Audio auferstehen
  // lassen, nachdem abort/reset/workspace-Wechsel lief).
  const generationRef = useRef(0)

  const revokeUrl = useCallback(() => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current)
      urlRef.current = null
    }
    setAudioUrl(null)
  }, [])

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    }
  }, [])

  const reset = useCallback(() => {
    generationRef.current += 1
    abortRef.current?.abort()
    abortRef.current = null
    stopPlayback()
    revokeUrl()
    setState('idle')
    setErrorMessage(null)
  }, [revokeUrl, stopPlayback])

  const requestSpeech = useCallback(async () => {
    const parsed = speechRequestSchema.safeParse({
      sessionId: message.session_id,
      messageId: message.id,
    })
    if (!parsed.success) {
      setState('error')
      setErrorMessage('Vorlesen nicht möglich: ungültige Nachricht.')
      return
    }

    // Jede neue Anfrage ist eine neue Generation — stale Antworten duerfen
    // danach weder State setzen noch eine URL erzeugen.
    const generation = ++generationRef.current
    const isCurrent = () => generationRef.current === generation

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    stopPlayback()
    revokeUrl()
    setState('loading')
    setErrorMessage(null)

    try {
      const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')
      const response = await fetch(`${baseUrl}/api/audio/speech`, {
        method: 'POST',
        headers: await buildAuthedHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(parsed.data),
        signal: controller.signal,
      })

      // Guard NACH den Headern: abgebrochene/stale Requests duerfen nicht weiter.
      if (!isCurrent() || controller.signal.aborted) return

      if (!response.ok) {
        let messageText = response.statusText
        try {
          const body = (await response.json()) as { error?: { message?: string } }
          messageText = body.error?.message ?? messageText
        } catch {
          /* ignore */
        }
        throw new ApiError(response.status, 'speech_failed', messageText)
      }

      const blob = await response.blob()
      // Guard nach dem Body-Read UND vor createObjectURL: eine inzwischen
      // abgelaufene Generation darf keine Object-URL mehr erzeugen.
      if (!isCurrent() || controller.signal.aborted) return
      if (blob.size === 0) {
        throw new ApiError(502, 'speech_failed', 'Leere Audio-Antwort.')
      }
      const url = URL.createObjectURL(blob)
      // Erneuter Guard direkt vor dem Setzen: zwischen createObjectURL und
      // setState darf kein reset/abort dazwischenkommen (sonst Leak).
      if (!isCurrent() || controller.signal.aborted) {
        URL.revokeObjectURL(url)
        return
      }
      urlRef.current = url
      setAudioUrl(url)
      setState('ready')
    } catch (err) {
      if (!isCurrent() || controller.signal.aborted) return
      setState('error')
      setErrorMessage(
        err instanceof ApiError && err.message
          ? err.message
          : 'Vorlesen fehlgeschlagen. Bitte erneut versuchen.',
      )
    } finally {
      if (abortRef.current === controller) abortRef.current = null
    }
  }, [message.id, message.session_id, revokeUrl, stopPlayback])

  const toggle = useCallback(() => {
    if (state === 'loading') {
      generationRef.current += 1
      abortRef.current?.abort()
      abortRef.current = null
      stopPlayback()
      revokeUrl()
      setState('idle')
      return
    }
    if (state === 'ready') {
      generationRef.current += 1
      stopPlayback()
      revokeUrl()
      setState('idle')
      return
    }
    void requestSpeech()
  }, [state, requestSpeech, revokeUrl, stopPlayback])

  const retry = useCallback(() => {
    void requestSpeech()
  }, [requestSpeech])

  // Reset bei Nachrichten- ODER Workspace-Wechsel (aktive Workspace-ID, nicht
  // nur message.workspace_id) und beim Unmount.
  const requestKey = `${activeWorkspaceId}:${message.workspace_id}:${message.id}`
  useEffect(() => {
    if (requestKeyRef.current !== requestKey) {
      requestKeyRef.current = requestKey
      generationRef.current += 1
      abortRef.current?.abort()
      abortRef.current = null
      stopPlayback()
      revokeUrl()
      setState('idle')
      setErrorMessage(null)
    }
  }, [requestKey, revokeUrl, stopPlayback])

  useEffect(() => () => {
    generationRef.current += 1
    abortRef.current?.abort()
    stopPlayback()
    if (urlRef.current) URL.revokeObjectURL(urlRef.current)
  }, [stopPlayback])

  return { state, audioUrl, errorMessage, audioRef, toggle, retry, reset }
}
